/**
 * JobPostForm Component
 * Form for employers to post new jobs
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import WorkIcon from '@mui/icons-material/Work';
import { 
  jobsService,
  OILFIELD_CERTIFICATIONS,
  JOB_TYPES,
  BASINS,
  COUNTIES
} from '@services/jobsService';
import { telegram } from '@services/telegram';
import { useAutoDismiss } from '../../hooks/useAutoDismiss';

interface JobPostFormProps {
  open: boolean;
  onClose: () => void;
  onPosted: () => void;
}

export function JobPostForm({ open, onClose, onPosted }: JobPostFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useAutoDismiss(error, () => setError(null));

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [jobType, setJobType] = useState('Full-time');
  const [location, setLocation] = useState('');
  const [basin, setBasin] = useState('Permian Basin');
  const [county, setCounty] = useState('');
  const [salaryMin, setSalaryMin] = useState<string>('');
  const [salaryMax, setSalaryMax] = useState<string>('');
  const [salaryType, setSalaryType] = useState('hourly');
  const [contactInfo, setContactInfo] = useState('');
  const [requiredCerts, setRequiredCerts] = useState<string[]>([]);
  const [experienceYears, setExperienceYears] = useState<string>('0');

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      setError('Job title is required');
      return;
    }
    if (!description.trim()) {
      setError('Job description is required');
      return;
    }
    if (!basin) {
      setError('Please select a basin');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await jobsService.createJob({
        title: title.trim(),
        description: description.trim(),
        jobType,
        location: location.trim(),
        basin,
        county,
        salary_min: salaryMin ? parseInt(salaryMin) : undefined,
        salary_max: salaryMax ? parseInt(salaryMax) : undefined,
        salary_type: salaryType,
        contactInfo: contactInfo.trim(),
        required_certifications: requiredCerts,
        required_experience_years: parseInt(experienceYears) || 0
      });

      if (result.success) {
        telegram.hapticFeedback('success');
        telegram.showAlert('Job posted successfully!');
        onPosted();
        resetForm();
      } else {
        setError(result.error || 'Failed to post job');
        telegram.hapticFeedback('error');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to post job');
      telegram.hapticFeedback('error');
    }

    setLoading(false);
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setJobType('Full-time');
    setLocation('');
    setBasin('Permian Basin');
    setCounty('');
    setSalaryMin('');
    setSalaryMax('');
    setSalaryType('hourly');
    setContactInfo('');
    setRequiredCerts([]);
    setExperienceYears('0');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e293b',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon sx={{ color: '#22c55e' }} />
          <Typography variant="h6">Post a Job</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Job Title */}
          <TextField
            fullWidth
            size="small"
            label="Job Title"
            placeholder="e.g., Experienced Driller, Roustabout, Operator"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            sx={textFieldSx}
          />

          {/* Job Type */}
          <FormControl fullWidth size="small">
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Job Type</InputLabel>
            <Select
              value={jobType}
              label="Job Type"
              onChange={(e) => setJobType(e.target.value)}
              sx={selectSx}
            >
              {JOB_TYPES.map(type => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Location */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Basin</InputLabel>
              <Select
                value={basin}
                label="Basin"
                onChange={(e) => setBasin(e.target.value)}
                sx={selectSx}
              >
                {BASINS.map(b => (
                  <MenuItem key={b} value={b}>{b}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>County</InputLabel>
              <Select
                value={county}
                label="County"
                onChange={(e) => setCounty(e.target.value)}
                sx={selectSx}
              >
                <MenuItem value="">Any County</MenuItem>
                {COUNTIES.map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Location Details"
            placeholder="e.g., 10 miles NE of Carlsbad, near Highway 285"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            sx={textFieldSx}
          />

          {/* Salary */}
          <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
            Compensation
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              label="Min"
              type="number"
              value={salaryMin}
              onChange={(e) => setSalaryMin(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              sx={{ ...textFieldSx, flex: 1 }}
            />
            <TextField
              size="small"
              label="Max"
              type="number"
              value={salaryMax}
              onChange={(e) => setSalaryMax(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              sx={{ ...textFieldSx, flex: 1 }}
            />
            <FormControl size="small" sx={{ width: 120 }}>
              <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Per</InputLabel>
              <Select
                value={salaryType}
                label="Per"
                onChange={(e) => setSalaryType(e.target.value)}
                sx={selectSx}
              >
                <MenuItem value="hourly">Hour</MenuItem>
                <MenuItem value="daily">Day</MenuItem>
                <MenuItem value="yearly">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Requirements */}
          <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
            Requirements
          </Typography>

          <Autocomplete
            multiple
            options={OILFIELD_CERTIFICATIONS}
            value={requiredCerts}
            onChange={(_, value) => setRequiredCerts(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip 
                  {...getTagProps({ index })} 
                  key={option}
                  label={option} 
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(245, 158, 11, 0.2)',
                    color: 'rgba(245, 158, 11, 0.9)'
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField 
                {...params} 
                size="small" 
                label="Required Certifications" 
                placeholder="Select certifications"
                sx={textFieldSx} 
              />
            )}
          />

          <TextField
            size="small"
            label="Minimum Experience (years)"
            type="number"
            value={experienceYears}
            onChange={(e) => setExperienceYears(e.target.value)}
            inputProps={{ min: 0, max: 50 }}
            sx={{ ...textFieldSx, width: 200 }}
          />

          {/* Description */}
          <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
            Job Details
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            size="small"
            label="Job Description"
            placeholder="Describe the role, responsibilities, schedule, and any other important details..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            sx={textFieldSx}
          />

          {/* Contact Info */}
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Contact Information"
            placeholder="How should applicants reach you? Phone, email, or other instructions..."
            value={contactInfo}
            onChange={(e) => setContactInfo(e.target.value)}
            sx={textFieldSx}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button 
          onClick={onClose} 
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
          onClick={handleSubmit}
          disabled={loading}
          sx={{ 
            backgroundColor: '#22c55e',
            '&:hover': { backgroundColor: '#16a34a' }
          }}
        >
          {loading ? 'Posting...' : 'Post Job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Common styles
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    '& input, & textarea': { color: 'white' },
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiInputAdornment-root': { color: 'rgba(255,255,255,0.5)' }
};

const selectSx = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  color: 'white',
  '& .MuiSvgIcon-root': { color: 'white' },
  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
  '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
};

export default JobPostForm;
